'use client';

import { useState } from 'react';
import { Loader2, FileText, FileType, FileCode, Download } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { useToast } from '@/hooks/use-toast';
import type { ExportFormat } from '@/lib/resume/types';

interface ResumeExportControlsProps {
  resumeId: string;
  disabled?: boolean;
}

const EXPORT_FORMATS: { value: ExportFormat; label: string; hint: string; icon: React.ReactNode }[] = [
  { value: 'pdf', label: 'PDF', hint: 'Sharing and printing', icon: <FileText className="h-4 w-4" /> },
  { value: 'docx', label: 'Word', hint: 'Editable in Word', icon: <FileType className="h-4 w-4" /> },
  { value: 'txt', label: 'Plain text', hint: 'ATS and copying', icon: <FileCode className="h-4 w-4" /> },
];

function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
}

/**
 * One-click download in each supported format.
 *
 * Deliberately has no primary submit button of its own: this sits below the
 * share controls in a share-first dialog, and a second call-to-action there
 * would compete with them.
 */
export function ResumeExportControls({ resumeId, disabled = false }: ResumeExportControlsProps) {
  const { toast } = useToast();
  const [pendingFormat, setPendingFormat] = useState<ExportFormat | null>(null);

  const handleDownload = async (format: ExportFormat) => {
    setPendingFormat(format);
    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format }),
      });

      if (!response.ok) {
        let errorMessage = 'Export failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData?.error || errorData?.details || errorMessage;
        } catch {
          // Keep the default message when the body is not valid JSON.
        }
        throw new Error(errorMessage);
      }

      const data = (await response.json()) as { data?: string; filename?: string; mimeType?: string };
      if (!data.data || !data.filename || !data.mimeType) {
        throw new Error('Invalid export response');
      }

      const url = URL.createObjectURL(base64ToBlob(data.data, data.mimeType));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = data.filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'Something went wrong while exporting the resume.',
        variant: 'destructive',
      });
    } finally {
      setPendingFormat(null);
    }
  };

  const busy = disabled || pendingFormat !== null;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium">Download a copy</p>
        <p className="text-xs text-muted-foreground">Saves to your device straight away.</p>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {EXPORT_FORMATS.map((format) => (
          <Button
            key={format.value}
            type="button"
            variant="outline"
            onClick={() => void handleDownload(format.value)}
            disabled={busy}
            className="h-auto flex-col items-start gap-0.5 px-3 py-2.5 text-left"
          >
            <span className="flex w-full items-center gap-2">
              {pendingFormat === format.value ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                format.icon
              )}
              <span className="text-label">{format.label}</span>
              {pendingFormat !== format.value && (
                <Download className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
              )}
            </span>
            <span className="text-caption font-normal normal-case text-muted-foreground">{format.hint}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
